import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useTransitionId } from '@confluence/route-manager';

import { HoverPreloaderCounter } from './HoverPreloaderCounter';

type TransitionPreloaderProps = {
	children: ReactNode;
	preloadUrl: string;
	source: string;
};

const PRELOAD_TIMER = 100;

const TransitionPreloaderImpl = ({ children, preloadUrl, source }: TransitionPreloaderProps) => {
	const hoverTimeoutRef = useRef<number | null>(null);
	const transitionId = useTransitionId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onHover = useCallback(() => {
		HoverPreloaderCounter.incrementHoverCounter(`${preloadUrl}-${transitionId}`);
		window.dispatchEvent(
			new CustomEvent('preloadOnHover', {
				bubbles: true,
				detail: { preloadUrl },
			}),
		);
	}, [preloadUrl, transitionId]);

	const fireHoverPreloaderAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'completed',
				actionSubject: 'hoverPreloader',
				source,
				attributes: {
					hoverCounter: HoverPreloaderCounter.getHoverCounter(`${preloadUrl}-${transitionId}`),
					hoverSource: `${preloadUrl}-${transitionId}`,
				},
			},
		}).fire();
		HoverPreloaderCounter.clearHoverCounter(`${preloadUrl}-${transitionId}`);
	}, [createAnalyticsEvent, preloadUrl, transitionId, source]);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				window.clearTimeout(hoverTimeoutRef.current);
			}
		};
	}, []);
	// temporarily adding analytics event for click on number of hovers
	// onClick to be removed
	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			data-testid="hover-preloader"
			onMouseEnter={() => {
				if (hoverTimeoutRef.current) {
					window.clearTimeout(hoverTimeoutRef.current);
				}
				hoverTimeoutRef.current = window.setTimeout(() => {
					onHover();
				}, PRELOAD_TIMER);
			}}
			onMouseLeave={() => {
				if (hoverTimeoutRef.current) {
					window.clearTimeout(hoverTimeoutRef.current);
				}
			}}
			onClick={fireHoverPreloaderAnalytics}
		>
			{children}
		</div>
	);
};

export const HoverPreloader = (props: TransitionPreloaderProps) => {
	if (!props.preloadUrl) {
		return <>{props.children}</>;
	}
	return <TransitionPreloaderImpl {...props} />;
};
